import * as React from "react";
import { Pressable, Text, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useCSSVariable } from "uniwind";
import { cn } from "@/shared/lib/utils";
import { BottomSheet } from "@/shared/components/ui/bottom-sheet";
import { Button } from "@/shared/components/ui/button";
import { Heading } from "@/shared/components/ui/heading";
import { IconButton } from "@/shared/components/ui/icon-button";

export type DateRange = { start: Date; end: Date };

type CalendarPickerModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedRange?: DateRange;
  onConfirm: (range: DateRange | undefined) => void;
};

const MONTH_NAMES = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

const DAY_LABELS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

// Strip time so comparisons are day-only
function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

export function CalendarPickerModal({
  open,
  onOpenChange,
  selectedRange,
  onConfirm,
}: CalendarPickerModalProps) {
  const today = React.useMemo(() => new Date(), []);
  const [displayMonth, setDisplayMonth] = React.useState(today.getMonth());
  const [displayYear, setDisplayYear] = React.useState(today.getFullYear());
  const [tempStart, setTempStart] = React.useState<Date | undefined>(
    selectedRange?.start
  );
  const [tempEnd, setTempEnd] = React.useState<Date | undefined>(
    selectedRange?.end
  );

  const brand = useCSSVariable("--color-brand") as string;

  React.useEffect(() => {
    if (open) {
      setTempStart(selectedRange?.start);
      setTempEnd(selectedRange?.end);
      const anchor = selectedRange?.start ?? today;
      setDisplayMonth(anchor.getMonth());
      setDisplayYear(anchor.getFullYear());
    }
  }, [open, selectedRange, today]);

  const daysInMonth = getDaysInMonth(displayYear, displayMonth);
  const firstDay = getFirstDayOfMonth(displayYear, displayMonth);

  const goToPrevMonth = () => {
    if (displayMonth === 0) {
      setDisplayMonth(11);
      setDisplayYear(displayYear - 1);
    } else {
      setDisplayMonth(displayMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (displayMonth === 11) {
      setDisplayMonth(0);
      setDisplayYear(displayYear + 1);
    } else {
      setDisplayMonth(displayMonth + 1);
    }
  };

  const handleDayPress = (day: number) => {
    const tapped = new Date(displayYear, displayMonth, day);

    // If no start, or both already set → start a new range
    if (!tempStart || tempEnd) {
      setTempStart(tapped);
      setTempEnd(undefined);
      return;
    }

    // Start exists but no end yet
    const tappedTime = startOfDay(tapped);
    const startTime = startOfDay(tempStart);

    if (tappedTime === startTime) {
      // Tap same day as start → deselect
      setTempStart(undefined);
    } else if (tappedTime < startTime) {
      // Tap before start → new start, old start becomes end
      setTempEnd(tempStart);
      setTempStart(tapped);
    } else {
      // Tap after start → set end
      setTempEnd(tapped);
    }
  };

  const handleConfirm = () => {
    if (tempStart && tempEnd) {
      onConfirm({ start: tempStart, end: tempEnd });
    } else {
      onConfirm(undefined);
    }
    onOpenChange(false);
  };

  const handleClear = () => {
    onConfirm(undefined);
    onOpenChange(false);
  };

  const hasRange = tempStart && tempEnd;

  // Build weeks — always 6 rows for consistent height
  const weeks: (number | null)[][] = [];
  let currentWeek: (number | null)[] = [];

  for (let i = 0; i < firstDay; i++) currentWeek.push(null);
  for (let day = 1; day <= daysInMonth; day++) {
    currentWeek.push(day);
    if (currentWeek.length === 7) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  }
  if (currentWeek.length > 0) {
    while (currentWeek.length < 7) currentWeek.push(null);
    weeks.push(currentWeek);
  }
  while (weeks.length < 6) weeks.push(Array(7).fill(null));

  const hint = !tempStart
    ? "Selecione a data de início"
    : !tempEnd
      ? "Selecione a data de fim"
      : null;

  return (
    <BottomSheet visible={open} onClose={() => onOpenChange(false)}>
      {/* Month navigation */}
      <View className="flex-row items-center justify-between px-5 pt-2 pb-1">
        <IconButton
          shape="circle"
          surface="transparent"
          accessibilityLabel="Mês anterior"
          onPress={goToPrevMonth}
        >
          <Ionicons name="chevron-back" size={22} color={brand} />
        </IconButton>
        <Heading variant="card">
          {MONTH_NAMES[displayMonth]} {displayYear}
        </Heading>
        <IconButton
          shape="circle"
          surface="transparent"
          accessibilityLabel="Próximo mês"
          onPress={goToNextMonth}
        >
          <Ionicons name="chevron-forward" size={22} color={brand} />
        </IconButton>
      </View>

      {/* Hint */}
      <View className="h-6 items-center justify-center">
        {hint && <Text className="text-muted-foreground text-xs">{hint}</Text>}
      </View>

      {/* Day-of-week labels */}
      <View className="flex-row px-3">
        {DAY_LABELS.map((label) => (
          <View key={label} className="flex-1 items-center py-2">
            <Text className="text-muted-foreground text-xs font-semibold uppercase">
              {label}
            </Text>
          </View>
        ))}
      </View>

      {/* Calendar grid */}
      <View className="px-3 pb-4">
        {weeks.map((week, weekIndex) => (
          <View key={weekIndex} className="flex-row">
            {week.map((day, dayIndex) => {
              if (day === null) {
                return (
                  <View
                    key={dayIndex}
                    className="relative flex-1 items-center py-1"
                  >
                    <View className="h-10 w-10" />
                  </View>
                );
              }

              const date = new Date(displayYear, displayMonth, day);
              const dateTime = startOfDay(date);
              const startTime = tempStart ? startOfDay(tempStart) : null;
              const endTime = tempEnd ? startOfDay(tempEnd) : null;

              const isRangeStart = tempStart
                ? isSameDay(date, tempStart)
                : false;
              const isRangeEnd = tempEnd ? isSameDay(date, tempEnd) : false;
              const isInRange =
                startTime !== null &&
                endTime !== null &&
                dateTime > startTime &&
                dateTime < endTime;
              const isToday = isSameDay(date, today);
              const isEndpoint = isRangeStart || isRangeEnd;

              return (
                <View
                  key={dayIndex}
                  className="relative flex-1 items-center py-1"
                >
                  {/* Range stripe — only when both dates are set */}
                  {hasRange && (isInRange || isEndpoint) && (
                    <View
                      className="bg-brand/20 absolute inset-y-1"
                      style={{
                        left: isRangeStart ? "50%" : 0,
                        right: isRangeEnd ? "50%" : 0,
                      }}
                    />
                  )}

                  {/* Day circle */}
                  <Pressable
                    onPress={() => handleDayPress(day)}
                    className={cn(
                      "z-10 h-10 w-10 items-center justify-center rounded-full",
                      isEndpoint && "bg-brand",
                      !isEndpoint && isToday && "border-brand border"
                    )}
                    accessibilityRole="button"
                    accessibilityLabel={`${day} ${MONTH_NAMES[displayMonth]}`}
                    accessibilityState={{ selected: isEndpoint }}
                  >
                    <Text
                      className={cn(
                        "text-sm font-medium",
                        isEndpoint ? "text-brand-foreground" : "text-foreground"
                      )}
                    >
                      {day}
                    </Text>
                  </Pressable>
                </View>
              );
            })}
          </View>
        ))}
      </View>

      {/* Action buttons */}
      <View className="border-border flex-row gap-3 border-t px-5 pt-4 pb-2">
        <Button variant="outline" className="flex-1" onPress={handleClear}>
          Limpar
        </Button>
        <Button className="flex-1" disabled={!hasRange} onPress={handleConfirm}>
          Confirmar
        </Button>
      </View>
    </BottomSheet>
  );
}
